import React, { useState } from "react";
import { BadgeCheck, AlertTriangle, ChevronDown, ChevronUp, ClipboardList, Database, Eye, Image as ImageIcon, Ban, RotateCcw, ScanEye } from "lucide-react";
import { Button } from "@/components/ui/button";

/* Badge di qualifica della pipeline unica Ambassador (qualificato ≥ soglia / da_verificare). */
export const AmbQualBadge = ({ amb, testid = "amb-qual-badge" }) => {
  const q = amb?.qualification;
  if (!q) return null;
  const base = "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold";
  if (q === "qualificato") {
    return <span data-testid={testid} className={`${base} bg-positive/15 text-positive`}><BadgeCheck className="h-3 w-3" /> Qualificato</span>;
  }
  if (q === "da_verificare") {
    return <span data-testid={testid} className={`${base} bg-amber-500/15 text-amber-600 dark:text-amber-400`}><AlertTriangle className="h-3 w-3" /> Da verificare</span>;
  }
  return null;
};

const VF_TONE = {
  HIGH: "bg-positive/15 text-positive",
  MEDIUM: "bg-blue-500/15 text-blue-700 dark:text-blue-300",
  LOW: "bg-danger/10 text-danger",
};

/* Visual Fit Brema (HIGH / MEDIUM / LOW) derivato dall'analisi visiva REALE dei contenuti. */
export const VisualFitBadge = ({ amb, testid = "amb-visual-fit-badge" }) => {
  if (!amb?.qualification) return null;
  const vf = amb?.visual_fit;
  const base = "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold";
  if (!vf) {
    return <span data-testid={testid} className={`${base} bg-muted text-muted-foreground`} title={amb?.visual_analysis?.reason || "Analisi visiva non disponibile"}><ScanEye className="h-3 w-3" /> Visual n.d.</span>;
  }
  const n = amb?.visual_analysis?.images_analyzed;
  return <span data-testid={testid} className={`${base} ${VF_TONE[vf] || "bg-muted"}`} title={amb?.visual_analysis?.visual_fit_reason || ""}><ScanEye className="h-3 w-3" /> Visual Fit {vf}{n ? ` · ${n} img` : ""}</span>;
};

const Stat = ({ label, value, tone = "", testid }) => (
  <div className="rounded-lg border border-border bg-brema-bg px-3 py-2" data-testid={testid}>
    <div className={`text-lg font-semibold leading-none ${tone}`}>{value ?? 0}</div>
    <div className="mt-1 text-[11px] text-muted-foreground">{label}</div>
  </div>
);

const VisualLine = ({ v }) => {
  if (!v) return null;
  if (!v.available) return <div className="text-[11px] text-muted-foreground">Analisi visiva: <b className="text-amber-600">non disponibile</b>{v.reason ? ` · ${v.reason}` : ""}</div>;
  return (
    <div className="text-[11px] text-muted-foreground">
      <span className="inline-flex items-center gap-1"><ImageIcon className="h-3 w-3" /> {v.images_analyzed} immagini reali analizzate ({v.model})</span>
      {v.summary && <div className="text-foreground/80">{v.summary}</div>}
    </div>
  );
};

/* Riepilogo dettagliato dell'ultima ricerca Ambassador (candidati, analizzati, esclusi con motivo, da verificare, qualificati, KB, visivo). */
export const AmbDiscoveryReportPanel = ({ report, compact = false, onClose }) => {
  const [open, setOpen] = useState(!compact);
  const [showExcluded, setShowExcluded] = useState(false);
  const [showVerify, setShowVerify] = useState(false);
  const [showQualified, setShowQualified] = useState(false);
  if (!report) return null;
  const kb = report.kb_applied || {};
  const ref = report.positive_examples_reference || {};
  return (
    <div className="rounded-xl border border-border bg-brema-card p-3" data-testid="amb-discovery-report">
      <div className="flex flex-wrap items-center gap-2">
        <ClipboardList className="h-4 w-4 text-brand" />
        <span className="text-sm font-semibold text-foreground">Riepilogo ricerca Ambassador</span>
        <span className="text-xs text-muted-foreground" data-testid="amb-report-headline">
          {report.qualified ?? 0}/{report.requested} qualificati · {report.analyzed ?? 0} analizzati su {report.candidates_found ?? 0} candidati
          {report.limit_reached ? ` · limite ${report.max_analyzed} raggiunto` : report.time_budget_reached ? " · tempo massimo raggiunto" : report.exhausted ? " · bacino esaurito" : ""}
        </span>
        <div className="ml-auto flex items-center gap-1">
          <button type="button" data-testid="amb-report-toggle" onClick={() => setOpen((v) => !v)}
            className="rounded-full p-1 text-muted-foreground hover:bg-brema-hover hover:text-foreground">
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {onClose && (
            <button type="button" data-testid="amb-report-close" onClick={onClose}
              className="rounded-full px-2 text-xs text-muted-foreground hover:bg-brema-hover hover:text-foreground">Chiudi</button>
          )}
        </div>
      </div>
      {open && (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
            <Stat label="Candidati trovati" value={report.candidates_found} testid="amb-report-candidates" />
            <Stat label={`Analizzati (max ${report.max_analyzed ?? 50})`} value={report.analyzed} testid="amb-report-analyzed" />
            <Stat label="Analisi visive" value={report.visual_analyzed_profiles} tone="text-blue-600 dark:text-blue-300" testid="amb-report-visual" />
            <Stat label={`Qualificati ≥${report.threshold ?? 70}`} value={report.qualified} tone="text-positive" testid="amb-report-qualified" />
            <Stat label="Da verificare" value={report.to_verify} tone="text-amber-600 dark:text-amber-400" testid="amb-report-to-verify" />
            <Stat label="Esclusi" value={report.excluded} tone="text-danger" testid="amb-report-excluded" />
          </div>
          <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground" data-testid="amb-report-kb">
            <Database className="h-3.5 w-3.5" />
            <span>Knowledge Base: Profilo target {kb.target_profile ? <b className="text-positive">applicato</b> : <b className="text-amber-600">non compilato</b>}</span>
            <span>· Regole di esclusione {kb.exclusion_rules ? <b className="text-positive">applicate</b> : <b className="text-amber-600">non compilate</b>}</span>
            <span>· Esempi positivi KB: {(kb.examples || []).length > 0
              ? <><b className="text-foreground">{kb.examples.map((h) => `@${h}`).join(", ")}</b>{ref.available ? <> → riferimento visivo <b className="text-positive">calcolato ({ref.images_analyzed} img reali)</b></> : <> → riferimento visivo <b className="text-amber-600">non calcolato</b></>}</>
              : <b className="text-amber-600">nessun link/handle nel Profilo target</b>}</span>
            <span>· Vision: <b className="text-foreground">{report.visual_model}</b> ({report.visual_images_total ?? 0} immagini)</span>
            <span>· Fascia follower {Array.isArray(report.follower_band) ? report.follower_band.map((n) => Number(n).toLocaleString("it-IT")).join("–") : ""}</span>
            {typeof report.duration_s === "number" && <span>· {report.duration_s}s · {report.cycles} cicli</span>}
          </div>
          {(kb.examples || []).length > 0 && !ref.available && ref.reason && <div className="text-[11px] text-amber-600" data-testid="amb-report-ref-reason">Riferimento visivo esempi: {ref.reason}</div>}
          {Array.isArray(report.expansion_used) && report.expansion_used.length > 0 && (
            <div className="text-[11px] text-muted-foreground" data-testid="amb-report-expansion">
              Discovery progressiva: bacino ampliato a {report.expansion_used.map((x) => x.type === "region" ? x.geo : x.discipline).filter((v, i, a) => a.indexOf(v) === i).join(", ")}
              {typeof report.preselect_discarded === "number" && report.preselect_discarded > 0 ? ` · ${report.preselect_discarded} scartati in pre-selezione (non pertinenti)` : ""}
            </div>
          )}
          {Array.isArray(report.qualified_details) && report.qualified_details.length > 0 && (
            <div>
              <button type="button" data-testid="amb-report-qualified-toggle" onClick={() => setShowQualified((v) => !v)} className="text-xs font-medium text-positive hover:underline">
                {showQualified ? "Nascondi" : "Mostra"} qualificati ed evidenze ({report.qualified_details.length})
              </button>
              {showQualified && (
                <ul className="mt-1.5 space-y-1" data-testid="amb-report-qualified-list">
                  {report.qualified_details.map((e, i) => (
                    <li key={i} className="rounded-lg border border-border bg-brema-bg px-2.5 py-1.5 text-xs">
                      <span className="font-medium text-foreground">@{e.handle}</span>
                      <span className="ml-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px]">Fit {e.fit_score}</span>
                      {e.visual_fit && <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] ${VF_TONE[e.visual_fit] || ""}`}>Visual {e.visual_fit}</span>}
                      {e.followers_verified && <span className="ml-1 text-muted-foreground">{Number(e.followers_verified).toLocaleString("it-IT")} follower</span>}
                      {e.discipline_verified && <span className="ml-1 text-muted-foreground">· {e.discipline_verified}</span>}
                      <ul className="mt-1 list-disc pl-4 text-muted-foreground">{(e.evidence || []).slice(0, 4).map((w, j) => <li key={j}>{w}</li>)}</ul>
                      <VisualLine v={e.visual} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {Array.isArray(report.excluded_details) && report.excluded_details.length > 0 && (
            <div>
              <button type="button" data-testid="amb-report-excluded-toggle" onClick={() => setShowExcluded((v) => !v)} className="text-xs font-medium text-danger hover:underline">
                {showExcluded ? "Nascondi" : "Mostra"} esclusi e motivi ({report.excluded_details.length})
              </button>
              {showExcluded && (
                <ul className="mt-1.5 space-y-1" data-testid="amb-report-excluded-list">
                  {report.excluded_details.map((e, i) => (
                    <li key={i} className="rounded-lg border border-border bg-brema-bg px-2.5 py-1.5 text-xs">
                      <span className="font-medium text-foreground">@{e.handle}</span>
                      {e.stage && <span className="ml-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px]">{e.stage}</span>}
                      <div className="text-muted-foreground">{e.reason}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {Array.isArray(report.to_verify_details) && report.to_verify_details.length > 0 && (
            <div>
              <button type="button" data-testid="amb-report-verify-toggle" onClick={() => setShowVerify((v) => !v)} className="text-xs font-medium text-amber-600 hover:underline dark:text-amber-400">
                {showVerify ? "Nascondi" : "Mostra"} da verificare ({report.to_verify_details.length})
              </button>
              {showVerify && (
                <ul className="mt-1.5 space-y-1" data-testid="amb-report-verify-list">
                  {report.to_verify_details.map((e, i) => (
                    <li key={i} className="rounded-lg border border-border bg-brema-bg px-2.5 py-1.5 text-xs">
                      <span className="font-medium text-foreground">@{e.handle}</span>
                      {typeof e.fit_score === "number" && <span className="ml-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px]">Fit {e.fit_score}</span>}
                      {e.visual_fit && <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] ${VF_TONE[e.visual_fit] || ""}`}>Visual {e.visual_fit}</span>}
                      <div className="text-muted-foreground">{e.reason}</div>
                      <VisualLine v={e.visual} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* Dettaglio qualificazione nel dialog profilo: evidenze, analisi visiva reale, sponsor/competitor, punteggi. */
export const AmbQualificationDetails = ({ amb }) => {
  if (!amb?.qualification) return null;
  const v = amb.visual_analysis || {};
  const s = amb.scores || {};
  const pf = amb.profile_facts || {};
  return (
    <div className="space-y-2 rounded-lg border border-border bg-brema-bg p-3" data-testid="amb-qualification-details">
      <div className="flex flex-wrap items-center gap-2">
        <AmbQualBadge amb={amb} testid="amb-detail-qual-badge" />
        <VisualFitBadge amb={amb} testid="amb-detail-visual-badge" />
        <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold">Fit {amb.fit_score}</span>
        {amb.discipline_verified && <span className="text-[11px] text-muted-foreground">Disciplina: <b className="text-foreground">{amb.discipline_verified}</b></span>}
      </div>
      {amb.description && <p className="text-xs text-foreground">{amb.description}</p>}
      {Array.isArray(amb.evidence) && amb.evidence.length > 0 && (
        <ul className="list-disc pl-4 text-xs text-muted-foreground" data-testid="amb-detail-evidence">{amb.evidence.map((e, i) => <li key={i}>{e}</li>)}</ul>
      )}
      {amb.kb_target_notes && <p className="text-[11px] text-muted-foreground"><Database className="mr-1 inline h-3 w-3" />KB: {amb.kb_target_notes}</p>}
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        {pf.days_since_last_post != null && <span>Ultimo post {pf.days_since_last_post} gg fa</span>}
        {pf.posts_per_month != null && <span>· {pf.posts_per_month} post/mese</span>}
        {pf.engagement_rate != null && <span>· engagement {pf.engagement_rate}%</span>}
        {pf.avg_likes != null && <span>· ~{Number(pf.avg_likes).toLocaleString("it-IT")} like</span>}
        {(amb.sponsors || []).length > 0 && <span>· Sponsor: {amb.sponsors.join(", ")}</span>}
        {(amb.competitor_apparel || []).length > 0 && <span className="text-danger">· Apparel concorrente: {amb.competitor_apparel.join(", ")}</span>}
      </div>
      {Object.keys(s).length > 0 && (
        <div className="flex flex-wrap gap-1.5 text-[10px]" data-testid="amb-detail-scores">
          {[["target_fit", "Target"], ["content_quality", "Qualità"], ["authenticity", "Autenticità"], ["activity", "Attività"], ["community", "Community"]].map(([k, l]) => (
            s[k] != null ? <span key={k} className="rounded-full bg-muted px-2 py-0.5">{l} {s[k]}</span> : null
          ))}
        </div>
      )}
      <div className="rounded-md border border-border bg-brema-card p-2 text-[11px]" data-testid="amb-detail-visual">
        <div className="mb-1 flex items-center gap-1 font-semibold text-foreground"><Eye className="h-3.5 w-3.5" /> Analisi visiva reale</div>
        {v.available ? (
          <>
            <div className="text-muted-foreground">{v.images_analyzed} immagini dei post recenti passate al modello vision <b className="text-foreground">{v.model}</b></div>
            {v.summary && <p className="mt-1 text-foreground/90">{v.summary}</p>}
            {v.visual_fit_reason && <p className="mt-1 text-muted-foreground">Visual Fit {v.visual_fit}: {v.visual_fit_reason}</p>}
            {Array.isArray(v.post_urls) && v.post_urls.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {v.post_urls.filter(Boolean).map((u, i) => <a key={i} href={u} target="_blank" rel="noopener noreferrer" className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-primary hover:underline">post {i + 1}</a>)}
              </div>
            )}
          </>
        ) : (
          <div className="text-amber-600">Non disponibile{v.reason ? ` · ${v.reason}` : ""} — nessun Visual Fit assegnato (non dedotto da testi/hashtag).</div>
        )}
      </div>
    </div>
  );
};

/* Registro degli Ambassador esclusi dalla pipeline (con motivo) + rivalutazione esplicita. */
export const ExcludedAmbassadorsPanel = ({ items, onReevaluate, onClear }) => {
  const [open, setOpen] = useState(false);
  if (!items?.length) return null;
  return (
    <div className="mt-4 rounded-2xl border border-border bg-brema-card p-4" data-testid="amb-excluded-box">
      <div className="flex flex-wrap items-center gap-2">
        <Ban className="h-4 w-4 text-danger" />
        <span className="text-sm font-semibold text-foreground">Esclusi dalla qualificazione ({items.length})</span>
        <span className="text-xs text-muted-foreground">non vengono riproposti finché non li rivaluti</span>
        <div className="ml-auto flex items-center gap-2">
          {onClear && <Button size="sm" variant="danger" data-testid="amb-excluded-clear-btn" onClick={onClear} className="rounded-full text-xs">Svuota registro</Button>}
          <button type="button" data-testid="amb-excluded-toggle" onClick={() => setOpen((v) => !v)} className="rounded-full p-1 text-muted-foreground hover:bg-brema-hover hover:text-foreground">
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>
      {open && (
        <ul className="mt-3 space-y-1.5" data-testid="amb-excluded-list">
          {items.map((e) => (
            <li key={e._id} className="flex flex-wrap items-start gap-2 rounded-lg border border-border bg-brema-bg px-3 py-2 text-xs" data-testid="amb-excluded-row">
              <div className="min-w-0 flex-1">
                <a href={e.url || `https://instagram.com/${e.handle}`} target="_blank" rel="noopener noreferrer" className="font-medium text-foreground hover:underline">@{e.handle}</a>
                {e.name && e.name !== e.handle && <span className="ml-1 text-muted-foreground">{e.name}</span>}
                {e.followers && <span className="ml-1 text-muted-foreground">· {Number(e.followers).toLocaleString("it-IT")} follower</span>}
                {e.stage && <span className="ml-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px]">{e.stage}</span>}
                <div className="text-muted-foreground">{e.reason}</div>
              </div>
              <Button size="sm" variant="secondary" data-testid="amb-excluded-reevaluate-btn" onClick={() => onReevaluate(e)}
                className="rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover"><RotateCcw className="mr-1 h-3 w-3" /> Rivaluta</Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
